import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/chillnn/cn";

import { DotsLoading } from "../loading/dots";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip/tooltip";

const buttonVariants = cva(
	"inline-flex items-center justify-center rounded-md text-sm font-medium tracking-wider ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "bg-zinc-900 text-zinc-50 hover:bg-zinc-900/90 border border-zinc-900",
				disabled: "bg-zinc-200 text-zinc-500 cursor-not-allowed border border-zinc-200",
				destructive:
					"bg-error-200 text-error-600 hover:bg-error-400/60 focus-visible:ring-error-600 border border-error-600",
				outline: "border font-medium border-zinc-900 bg-white hover:bg-zinc-100 hover:text-zinc-900",
				"no-outline": "font-medium bg-white hover:bg-zinc-100 hover:text-zinc-900 border border-transparent",
				"input-ish": "border font-medium text-zinc-800 border-zinc-200 bg-white hover:bg-zinc-100",
				link: "text-zinc-900 underline-offset-4 hover:underline border border-transparent",
				"link-blue": "text-link-400 underline-offset-4 hover:underline border border-transparent",

				"status-in-sale": " bg-before-status-blue  text-zinc-50 hover:bg-before-status-blue/80",
				"status-not-for-sale":
					" bg-error-200 text-error-600 hover:bg-error-400/60 focus-visible:ring-error-600",
			},
			size: {
				default: "h-10 px-4 py-2",
				"2xs": "h-6",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				xl: "h-11 rounded-md px-14",
				icon: "h-9 w-9",
			},
			textAlign: {
				left: "text-left flex justify-start",
				center: "text-center",
				right: "text-right",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
			textAlign: "center",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	children: React.ReactNode;
	asChild?: boolean;
	loading?: boolean;
	tooltip?: string;
	icon?: React.ReactNode;
}

export type ButtonVariantType = React.ComponentProps<typeof Button>["variant"];

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			textAlign,
			asChild = false,
			loading = false,
			children,
			tooltip,
			icon,
			onClick,
			type = "button",
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";

		const loadingBGColor = variant === "outline" ? "zinc" : "default";

		/**
		 * @memo `variant === disabled` の場合に tailwindcss で `pointer-events-none` を設定すると tooltip が表示されない
		 * @memo これを回避するために `handleClick`を定義し、`variant === disabled`の場合には処理を実行しないようにしている
		 */
		const handleClick = React.useCallback(
			(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				if (loading || variant === "disabled") return;

				onClick && onClick(event);
			},
			[loading, onClick, variant],
		);

		return (
			<>
				{tooltip ? (
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Comp
									className={cn(buttonVariants({ variant, size, className, textAlign }))}
									ref={ref}
									onClick={handleClick}
									type={type}
									{...props}
								>
									{loading ? (
										<DotsLoading bgColor={loadingBGColor} />
									) : (
										<>
											{children}
											{icon && <span className="ml-2">{icon}</span>}
										</>
									)}
								</Comp>
							</TooltipTrigger>
							<TooltipContent sideOffset={8}>{tooltip}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				) : (
					<Comp
						className={cn(buttonVariants({ variant, size, className, textAlign }))}
						ref={ref}
						onClick={handleClick}
						type={type}
						{...props}
					>
						{loading ? (
							<DotsLoading bgColor={loadingBGColor} />
						) : (
							<>
								{children}
								{icon && <span className="ml-2">{icon}</span>}
							</>
						)}
					</Comp>
				)}
			</>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
